<template>
  <woot-modal
    :show="true"
    :on-close="onClose"
    size="!w-[700px] !max-w-[95vw] !rounded-[12px]"
  >
    <div class="flex flex-col max-h-[90vh]">
      <!-- Stepper -->
      <div class="flex items-center justify-center gap-3 pt-8 pb-2 px-6">
        <div class="flex items-center gap-2">
          <span
            class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold"
            :class="
              step >= 1
                ? 'bg-[#2ABB7F] text-white'
                : 'bg-gray-200 text-gray-500'
            "
          >
            <fluent-icon v-if="step > 1" icon="checkmark" size="14" />
            <template v-else>1</template>
          </span>
          <span
            class="text-sm"
            :class="
              step === 1 ? 'font-semibold text-[#2ABB7F]' : 'text-gray-500'
            "
          >
            {{ $t('TEAMS_SETTINGS.EDIT_MODAL.STEPS.DETAILS') }}
          </span>
        </div>
        <span class="w-12 h-px bg-gray-200" />
        <div class="flex items-center gap-2">
          <span
            class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold"
            :class="
              step >= 2
                ? 'bg-[#2ABB7F] text-white'
                : 'bg-gray-200 text-gray-400'
            "
          >
            2
          </span>
          <span
            class="text-sm"
            :class="
              step === 2 ? 'font-semibold text-[#2ABB7F]' : 'text-gray-400'
            "
          >
            {{ $t('TEAMS_SETTINGS.EDIT_MODAL.STEPS.AGENTS') }}
          </span>
        </div>
      </div>

      <!-- STEP 1: team details -->
      <form
        v-if="step === 1"
        class="flex flex-col w-full !p-0 !self-stretch"
        @submit.prevent="onNext"
      >
        <!-- header block -->
        <div
          class="px-6 py-4 border-b border-[#F3F4F6]"
          style="box-shadow: 0 1px 4px #cacaca29"
        >
          <h3 class="text-lg font-semibold text-gray-800">
            {{ detailsTitle }}
          </h3>
          <p class="text-sm text-gray-500 mt-1">
            {{ detailsDesc }}
          </p>
        </div>

        <div class="flex flex-col px-6 py-4">
          <label class="block text-sm text-gray-700 mb-1">
            {{ $t('TEAMS_SETTINGS.EDIT_MODAL.NAME_LABEL') }}
            <span class="text-red-500">*</span>
          </label>
          <input
            v-model.trim="title"
            type="text"
            class="w-full h-11 rounded-[8px] border px-3 text-sm mb-4"
            :class="$v.title.$error ? 'border-red-400' : 'border-[#9CA3AF]'"
            :placeholder="$t('TEAMS_SETTINGS.EDIT_MODAL.NAME_PLACEHOLDER')"
            @input="$v.title.$touch"
          />

          <label class="block text-sm text-gray-700 mb-1">
            {{ $t('TEAMS_SETTINGS.EDIT_MODAL.DESC_LABEL') }}
          </label>
          <textarea
            v-model.trim="description"
            rows="3"
            class="w-full rounded-[8px] border border-[#9CA3AF] p-3 text-sm mb-4 resize-none"
            :placeholder="$t('TEAMS_SETTINGS.EDIT_MODAL.DESC_PLACEHOLDER')"
          />

          <label
            class="flex items-start gap-2 rounded-[12px] border p-4 cursor-pointer"
            :class="
              allowAutoAssign
                ? 'border-[#14B8A6] bg-[#F0FDFA]'
                : 'border-gray-200'
            "
          >
            <input v-model="allowAutoAssign" type="checkbox" class="sr-only" />
            <check-tick :checked="allowAutoAssign" class="mt-0.5" />
            <span>
              <span class="block text-sm font-medium text-gray-800">
                {{ $t('TEAMS_SETTINGS.EDIT_MODAL.AUTO_ASSIGN.LABEL') }}
              </span>
              <span class="block text-xs text-gray-500">
                {{ $t('TEAMS_SETTINGS.EDIT_MODAL.AUTO_ASSIGN.HELP') }}
              </span>
            </span>
          </label>
        </div>

        <!-- footer -->
        <div
          class="flex items-center justify-end gap-3 border-t border-[#F3F4F6] px-6 pt-4 pb-6"
        >
          <button
            type="button"
            class="button-ghost px-5 py-3 text-sm rounded-[5px]"
            style="border: 1px solid #e5e7eb"
            @click="onClose"
          >
            {{ $t('TEAMS_SETTINGS.EDIT_MODAL.CANCEL') }}
          </button>
          <button
            type="button"
            class="button-filled px-5 py-3 text-sm rounded-[5px]"
            :disabled="$v.title.$invalid || memberUiFlags.isFetching"
            @click="onNext"
          >
            {{ $t('TEAMS_SETTINGS.EDIT_MODAL.NEXT') }}
          </button>
        </div>
      </form>

      <!-- STEP 2: agents -->
      <div v-else class="flex flex-col min-h-0">
        <!-- header block -->
        <div
          class="px-6 py-4 border-b border-[#F3F4F6]"
          style="box-shadow: 0 1px 4px #cacaca29"
        >
          <h3 class="text-lg font-semibold text-gray-800">
            {{ $t('TEAMS_SETTINGS.EDIT_MODAL.AGENTS.TITLE', { teamName }) }}
          </h3>
          <p class="text-sm text-gray-500 mt-1">
            {{ $t('TEAMS_SETTINGS.EDIT_MODAL.AGENTS.DESC') }}
          </p>
        </div>

        <div class="flex flex-col px-6 py-4 min-h-0">
          <!-- search + role filter -->
          <div class="flex items-center gap-2 mb-3">
            <div class="relative flex-1">
              <fluent-icon
                icon="search"
                size="16"
                class="absolute ltr:left-3 rtl:right-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                v-model="searchQuery"
                type="text"
                class="w-full h-11 rounded-[8px] border border-[#9CA3AF] ltr:pl-9 rtl:pr-9 text-sm !m-0"
                :placeholder="
                  $t('TEAMS_SETTINGS.EDIT_MODAL.SEARCH_PLACEHOLDER')
                "
              />
            </div>

            <!-- multi-select role filter -->
            <div class="relative w-[180px]">
              <button
                type="button"
                class="w-full h-11 rounded-[8px] px-3 text-sm bg-white flex items-center justify-between"
                style="border: 1px solid #d1d5db"
                @click="showRoleDropdown = !showRoleDropdown"
              >
                <span class="truncate">
                  {{ $t('TEAMS_SETTINGS.EDIT_MODAL.ALL_ROLES') }}
                </span>
                <fluent-icon
                  icon="chevron-down"
                  size="14"
                  class="text-gray-400 shrink-0"
                />
              </button>
              <template v-if="showRoleDropdown">
                <div
                  class="fixed inset-0 z-10"
                  @click="showRoleDropdown = false"
                />
                <div
                  class="absolute z-20 mt-1 w-[200px] bg-white border border-gray-100 rounded-[8px] shadow-lg py-1 ltr:right-0 rtl:left-0"
                >
                  <label
                    class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      class="sr-only"
                      :checked="isAllRoles"
                      @change="toggleAllRoles"
                    />
                    <check-tick :checked="isAllRoles" />
                    {{ $t('TEAMS_SETTINGS.EDIT_MODAL.ALL_ROLES') }}
                  </label>
                  <label
                    v-for="role in roles"
                    :key="role"
                    class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      class="sr-only"
                      :checked="selectedRoles.includes(role)"
                      @change="toggleRole(role)"
                    />
                    <check-tick :checked="selectedRoles.includes(role)" />
                    {{ translatedRole(role) }}
                  </label>
                </div>
              </template>
            </div>
          </div>

          <!-- selected count + clear all (gray bar) -->
          <div
            class="flex items-center justify-between h-12 px-3 rounded-[8px] bg-[#FAFAFA] mb-2"
          >
            <span class="text-sm text-gray-600">
              {{
                $t('TEAMS_SETTINGS.EDIT_MODAL.SELECTED', {
                  count: selectedAgents.length,
                })
              }}
            </span>
            <button
              type="button"
              class="text-sm text-[#2ABB7F] px-2 py-1 rounded-[6px] cursor-pointer hover:bg-[#F0FDF4]"
              @click="clearAll"
            >
              {{ $t('TEAMS_SETTINGS.EDIT_MODAL.CLEAR_ALL') }}
            </button>
          </div>

          <!-- showing + select all -->
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs text-gray-500">
              {{
                $t('TEAMS_SETTINGS.EDIT_MODAL.SHOWING_AGENTS', {
                  count: filteredAgents.length,
                })
              }}
            </span>
            <button
              type="button"
              class="h-8 rounded-[4px] px-3 text-sm text-gray-700 bg-white cursor-pointer hover:bg-[#F9FAFB]"
              style="border: 1px solid #e5e7eb"
              @click="selectAll"
            >
              {{ $t('TEAMS_SETTINGS.EDIT_MODAL.SELECT_ALL') }}
            </button>
          </div>

          <!-- agent list -->
          <div class="flex flex-col gap-1 overflow-auto max-h-[280px]">
            <label
              v-for="agent in filteredAgents"
              :key="agent.id"
              class="flex items-center gap-3 h-[60px] px-2 rounded-[8px] border cursor-pointer shrink-0"
              :class="
                isSelected(agent.id)
                  ? 'border-[#DCFFF1] bg-[#F0FDF4]'
                  : 'border-[#E5E7EB] hover:bg-gray-50'
              "
            >
              <input
                type="checkbox"
                class="sr-only"
                :checked="isSelected(agent.id)"
                @change="toggleAgent(agent.id)"
              />
              <check-tick :checked="isSelected(agent.id)" />
              <thumbnail
                :src="agent.thumbnail"
                size="32px"
                :username="agent.name"
                :status="agent.availability_status"
              />
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-gray-800 truncate">
                  {{ agent.name }}
                </div>
                <div class="text-xs text-gray-500 truncate">
                  {{ agent.email }}
                </div>
              </div>
              <span
                v-if="agent.role"
                class="semantic-tag text-xs whitespace-nowrap"
                :class="roleColor(agent.role)"
              >
                {{ translatedRole(agent.role) }}
              </span>
            </label>
          </div>
        </div>

        <!-- footer -->
        <div
          class="flex items-center justify-between border-t border-[#F3F4F6] px-6 pt-4 pb-6"
        >
          <button
            type="button"
            class="button-ghost px-5 py-3 text-sm rounded-[5px]"
            style="border: 1px solid #e5e7eb"
            @click="onPrevious"
          >
            {{ $t('TEAMS_SETTINGS.EDIT_MODAL.PREVIOUS') }}
          </button>
          <button
            type="button"
            class="button-filled px-5 py-3 text-sm rounded-[5px]"
            :disabled="isSaving"
            @click="onSave"
          >
            {{ submitLabel }}
          </button>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import alertMixin from 'shared/mixins/alertMixin';
import validations from './helpers/validations';
import CheckTick from './CheckTick.vue';

const ROLE_COLORS = {
  'General Manager': 'tag-primary-01',
  Admin: 'tag-primary-02',
  Marketing: 'tag-primary-06',
  'Shift Manager': 'tag-primary-04',
  Agent: 'tag-primary-03',
};

export default {
  components: { Thumbnail, CheckTick },
  mixins: [alertMixin],
  props: {
    team: {
      type: Object,
      default: () => ({}),
    },
    onClose: {
      type: Function,
      required: true,
    },
  },
  validations,
  data() {
    return {
      step: 1,
      title: this.team.name || '',
      description: this.team.description || '',
      allowAutoAssign:
        this.team.allow_auto_assign === undefined
          ? true
          : this.team.allow_auto_assign,
      searchQuery: '',
      selectedRoles: [],
      showRoleDropdown: false,
      selectedAgents: [],
    };
  },
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
      teamUiFlags: 'teams/getUIFlags',
      memberUiFlags: 'teamMembers/getUIFlags',
    }),
    isEdit() {
      return !!this.team.id;
    },
    teamName() {
      return this.title;
    },
    detailsTitle() {
      return this.$t(
        this.isEdit
          ? 'TEAMS_SETTINGS.EDIT_MODAL.DETAILS.TITLE'
          : 'TEAMS_SETTINGS.EDIT_MODAL.CREATE_TITLE'
      );
    },
    detailsDesc() {
      return this.$t(
        this.isEdit
          ? 'TEAMS_SETTINGS.EDIT_MODAL.DETAILS.DESC'
          : 'TEAMS_SETTINGS.EDIT_MODAL.CREATE_DESC'
      );
    },
    submitLabel() {
      return this.$t(
        this.isEdit
          ? 'TEAMS_SETTINGS.EDIT_MODAL.SAVE'
          : 'TEAMS_SETTINGS.EDIT_MODAL.CREATE_SUBMIT'
      );
    },
    isSaving() {
      return (
        this.teamUiFlags.isCreating ||
        this.teamUiFlags.isUpdating ||
        this.memberUiFlags.isUpdating
      );
    },
    roles() {
      return [...new Set(this.agents.map(agent => agent.role).filter(Boolean))];
    },
    isAllRoles() {
      return this.selectedRoles.length === 0;
    },
    filteredAgents() {
      const query = this.searchQuery.trim().toLowerCase();
      return this.agents.filter(agent => {
        const matchesRole =
          this.isAllRoles || this.selectedRoles.includes(agent.role);
        const matchesSearch =
          !query ||
          (agent.name || '').toLowerCase().includes(query) ||
          (agent.email || '').toLowerCase().includes(query);
        return matchesRole && matchesSearch;
      });
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
  },
  methods: {
    isSelected(id) {
      return this.selectedAgents.includes(id);
    },
    toggleAgent(id) {
      this.selectedAgents = this.isSelected(id)
        ? this.selectedAgents.filter(agentId => agentId !== id)
        : [...this.selectedAgents, id];
    },
    selectAll() {
      this.selectedAgents = this.filteredAgents.map(agent => agent.id);
    },
    clearAll() {
      this.selectedAgents = [];
    },
    toggleRole(role) {
      this.selectedRoles = this.selectedRoles.includes(role)
        ? this.selectedRoles.filter(item => item !== role)
        : [...this.selectedRoles, role];
    },
    toggleAllRoles() {
      this.selectedRoles = [];
    },
    roleColor(role) {
      return ROLE_COLORS[role] || 'tag-primary-03';
    },
    translatedRole(role) {
      const key = `AGENT_MGMT.AGENT_TYPES.${role && role.toUpperCase()}`;
      const translation = this.$t(key);
      return translation !== key ? translation : role;
    },
    async loadMembers() {
      try {
        await this.$store.dispatch('teamMembers/get', { teamId: this.team.id });
        this.selectedAgents = this.$store.getters['teamMembers/getTeamMembers'](
          this.team.id
        ).map(member => member.id);
      } catch {
        this.selectedAgents = [];
      }
    },
    async onNext() {
      this.$v.$touch();
      if (this.$v.title.$invalid) {
        return;
      }
      // Don't persist yet — everything is saved together on the final step.
      // In edit mode, pre-select the team's current members.
      if (this.isEdit) {
        await this.loadMembers();
      }
      this.step = 2;
    },
    onPrevious() {
      this.step = 1;
    },
    async onSave() {
      try {
        const teamPayload = {
          name: this.title,
          description: this.description,
          allow_auto_assign: this.allowAutoAssign,
        };
        let teamId = this.team.id;
        if (this.isEdit) {
          await this.$store.dispatch('teams/update', {
            id: teamId,
            ...teamPayload,
          });
        } else {
          const createdTeam = await this.$store.dispatch(
            'teams/create',
            teamPayload
          );
          teamId = createdTeam.id;
        }
        await this.$store.dispatch('teamMembers/update', {
          teamId,
          agentsList: this.selectedAgents,
        });
        this.$store.dispatch('teams/get');
        this.showAlert(
          this.$t(
            this.isEdit
              ? 'TEAMS_SETTINGS.EDIT_MODAL.SUCCESS'
              : 'TEAMS_SETTINGS.EDIT_MODAL.SUCCESS_CREATE'
          )
        );
        this.onClose();
      } catch (error) {
        this.showAlert(
          error.message || this.$t('TEAMS_SETTINGS.TEAM_FORM.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>
